import React, { FC, useCallback, useState } from 'react'
import { Typography } from 'react-components'
import { CTAButton, StyledGhostButton } from '../style'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import get from 'lodash/get'
import getChildrenAges from '../utils/get-children-ages'
import useDynamicQueryParams from '../hooks/use-dynamic-query-params'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import AnalyticsService from '../../../services/analytics-service'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import { MailPickerIcon } from '../../search-result-listing/header-section/searchbar/widget-styles'
import { useTranslation } from '../../../hooks/locale'
import UserDetailsModel from '../../../models/user/user-details-model'
import { PdpToCheckoutPosition } from '../constants'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { usePropertyDetailsContext } from '../../../hooks/property-details'
import { getCategory } from '../../../utils/category-utils'
import { useRouter } from 'next/router'

type EnquiryButtonProps = {
    bookingType: FormattedBookingTypeData | null
    searchbar: SearchbarFieldsReturn
    showShortTitle?: boolean
    position:
        | PdpToCheckoutPosition.GALLERY
        | PdpToCheckoutPosition.SIDEBAR
        | PdpToCheckoutPosition.STICKY_FOOTER
        | PdpToCheckoutPosition.PRICE_WIDGET
    inquiryContactDetails: InquiryContactDetailsTransport | null
    pricingDetails: FormattedLivePricingDataTransport | null
    isPricingLoading: boolean
    isAvailabilityLoading: boolean
    pricingErrorCode: number | null
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const EnquiryButton: FC<EnquiryButtonProps> = props => {
    const { bookingType, searchbar, position, showShortTitle = false, ...restProps } = props

    const { query } = useRouter()
    const dynamicQueryParams = JSON.parse(get(bookingType, 'subType.dynamicQueryParams', '{}'))
    const childrenAges = getChildrenAges(searchbar.kids)
    const qpValues = { ...query, ...searchbar, childrenAges }
    const url = useDynamicQueryParams(bookingType?.subType, qpValues, dynamicQueryParams)
    const type = get(bookingType, 'subType.type')
    const titleKey = showShortTitle ? 'subType.shortTitle' : 'subType.title'
    const { propertyDetails } = usePropertyDetailsContext()
    const { t } = useTranslation()
    const { isComboListing } = usePropertyDetailsContext()

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isEnquiryPopupOpen, setEnquiryPopupVisibility] = useState(false)
    const [EnquiryPopup, setEnquiryPopup] = useState<React.ComponentType<any> | null>(null)

    const {
        isProvidedVariantActive,
        isLoading: abLoading,
        error,
    } = useABVariant(AB_KEY.CHECKOUT_MERGER, AB_VARIANT_VERSION.ONE)

    const showEnquiryPopup = useCallback(async () => {
        if (!EnquiryPopup) {
            setIsLoading(true)
            const module = await import('./enquiry-popup')
            setEnquiryPopup(() => module.default)
            setIsLoading(false)
        }
        setEnquiryPopupVisibility(true)
    }, [EnquiryPopup])

    const hideEnquiryPopup = useCallback(() => {
        setEnquiryPopupVisibility(false)
    }, [])

    const onEnquiryClick = useCallback(
        (event: React.MouseEvent) => {
            setTimeout(() => {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.ENQUIRY_BUTTON_CLICKED,
                    label: position,
                    value: Date.now().toString(),
                })
            }, 0)

            if (type === 'redirect') {
                event.preventDefault()
                setIsLoading(true)
                window.open(url, '_blank')
                setIsLoading(false)
            } else {
                if (isProvidedVariantActive || propertyDetails?.general.tfwObjectId === '121') {
                    event.preventDefault()
                    showEnquiryPopup()
                } else {
                    setIsLoading(true)
                    window.location.href = url
                    setTimeout(() => {
                        setIsLoading(false)
                    }, 5000)
                }
            }
        },
        [type, url, position, showEnquiryPopup, isProvidedVariantActive, isComboListing],
    )

    return (
        <>
            <CTAButton>
                <StyledGhostButton
                    isPrimary
                    onClick={onEnquiryClick}
                    disabled={isLoading || abLoading ? true : undefined}
                    loading={isLoading || abLoading ? true : undefined}
                    buttonSize="large"
                >
                    <MailPickerIcon alt={t('mailIconText')} style={{ position: 'unset' }} />
                    <Typography variant="subtitle2" fontWeight="semiBold">
                        {get(bookingType, titleKey)}
                    </Typography>
                </StyledGhostButton>
            </CTAButton>
            {!!EnquiryPopup && (
                <EnquiryPopup
                    isOpen={isEnquiryPopupOpen}
                    onClose={hideEnquiryPopup}
                    searchbar={searchbar}
                    {...restProps}
                />
            )}
        </>
    )
}

export default EnquiryButton
